import { type FC, type MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import { Alert, Button, Checkbox, Skeleton, Stack, Text, TextInput } from "@mantine/core";
import { SearchIcon } from "@snapchat/snap-design-system-icons";
import { type CellContext, type ColumnDef, type SortingState } from "@tanstack/react-table";
import { clsx } from "clsx";
import { useIntl } from "react-intl";

import { AbsoluteRoute } from "../../../core/routes/absoluteRoutes";
import { PayoutDataError } from "../../../lens-creator-payout/components/messages.generated";
import { useLensIdSearchParameter } from "../../../lens-insights/hooks/useLensIdSearchParameter";
import { VirtualTable } from "../../../shared/components/virtual-table";
import { type useLensPlusActivityTotals } from "../../hooks/useLensPlusActivityTotals";
import { type useLensPlusMonthlyScores } from "../../hooks/useLensPlusMonthlyScores";
import { type LensPlusTableRow } from "../../models/LensPlusTableRow";

import { lensPlusLensColumns } from "./LensPlusLensColumns";
import styles from "./LensPlusTab.module.css";
import { NoLensesToDisplay, messages } from "./messages.generated";

const LensSelectionCell: FC<CellContext<LensPlusTableRow, unknown>> = ({ row: { original } }) => {
    const intl = useIntl();
    const { lensId, setLensId, clearLensId } = useLensIdSearchParameter();
    return (
        <Checkbox
            aria-label={intl.formatMessage(messages.inspectLens, {
                name: original.lens?.name ?? original.unlockableId,
            })}
            disabled={!original.lens}
            checked={Boolean(original.lens && original.lens.id === lensId)}
            onChange={() => {
                if (original.lens?.id === lensId) clearLensId();
                else if (original.lens) setLensId(original.lens.id);
            }}
        />
    );
};

const FLEX_COLUMNS = ["name"];
const lensRowId = (row: LensPlusTableRow): string => row.unlockableId;
export const LensPlusLensTable: FC<{
    month: string;
    allTime: boolean;
    minimum: number;
    monthly: ReturnType<typeof useLensPlusMonthlyScores>;
    activity: ReturnType<typeof useLensPlusActivityTotals>;
}> = ({ month, allTime, minimum, monthly, activity }) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [sorting, setSorting] = useState<SortingState>([{ id: "score", desc: true }]);
    const handleRowClick = useCallback(
        (row: LensPlusTableRow) =>
            row.lens
                ? (event?: MouseEvent) => {
                      if (event?.target instanceof Element && event.target.closest("a, button, input, label")) return;
                      if (row.lens) navigate(generatePath(AbsoluteRoute.LENS, { lensId: row.lens.id }));
                  }
                : undefined,
        [navigate],
    );
    const list = allTime ? activity : monthly;
    const {
        hasMore,
        fetchMore: loadMore,
        refetch,
        loading,
        loadingMore: paging,
        hasError: initialError,
        hasRefreshError,
        hasMoreError,
    } = list;
    const rows = useMemo<LensPlusTableRow[]>(
        () =>
            allTime
                ? activity.rows.map((row) => ({ ...row, window: null }))
                : monthly.rows.map((row) => ({
                      unlockableId: String(row.unlockableId),
                      lens: row.lens,
                      totals: row.selectedMonth.totals,
                      window: row.selectedMonth,
                  })),
        [allTime, activity.rows, monthly.rows],
    );
    const hasError = initialError || hasRefreshError || hasMoreError;
    // Fetch sequentially so search and sorting cover every Lens without blocking the first page.
    useEffect(() => {
        if (hasMore && !loading && !paging && !hasError) loadMore();
    }, [hasMore, loading, paging, hasError, loadMore]);
    const columns = useMemo<ColumnDef<LensPlusTableRow>[]>(
        () => [
            {
                id: "select",
                header: () => null,
                size: 40,
                enableSorting: false,
                enableGlobalFilter: false,
                cell: LensSelectionCell,
            },
            ...lensPlusLensColumns(
                allTime
                    ? intl.formatMessage(messages.allTimeLenses)
                    : intl.formatDate(new Date(`${month}-01T00:00:00Z`), { month: "long", timeZone: "UTC" }),
                minimum,
                `month=${month}`,
                allTime,
            ),
        ],
        [intl, month, allTime, minimum],
    );
    return (
        <Stack gap="sm">
            <TextInput
                leftSection={<SearchIcon />}
                value={search}
                aria-label={intl.formatMessage(messages.searchLenses)}
                placeholder={intl.formatMessage(messages.searchLenses)}
                onChange={(event) => {
                    setSearch(event.currentTarget.value);
                }}
            />
            {allTime && <Text className="paragraph-p300">{intl.formatMessage(messages.allTimeLensesNote)}</Text>}
            {hasError && (
                <Alert color="red">
                    <PayoutDataError />
                    <Button variant="subtle" onClick={hasMoreError ? loadMore : refetch}>
                        {intl.formatMessage(messages.retry)}
                    </Button>
                </Alert>
            )}
            {!hasError && (hasMore || loading || paging) && (
                <Text role="status" className={clsx("paragraph-p300", styles.loadingNote)}>
                    {intl.formatMessage(messages.lensPages, {
                        count: rows.length,
                    })}
                </Text>
            )}
            {rows.length === 0 && loading ? (
                <Skeleton h={320} />
            ) : (
                <div className={styles.scroll}>
                    <VirtualTable<LensPlusTableRow>
                        maxHeight="60vh"
                        data={rows}
                        columns={columns}
                        getRowId={lensRowId}
                        flexColumns={FLEX_COLUMNS}
                        globalFilter={search}
                        onGlobalFilterChange={setSearch}
                        sorting={sorting}
                        onSortingChange={setSorting}
                        onRowClick={handleRowClick}
                        emptyMessage={
                            <Text className="paragraph-p300">
                                {hasMore ? (
                                    intl.formatMessage(
                                        hasError ? messages.incompleteLensMatches : messages.loadingLensMatches,
                                    )
                                ) : (
                                    <NoLensesToDisplay />
                                )}
                            </Text>
                        }
                    />
                </div>
            )}
        </Stack>
    );
};
